import {
  getWorkspaceId,
  getWaConfig,
  getWorkspace,
  store,
  useAppSelector,
  usePublishMessageMutation,
} from '@zonofi/zono-money-store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { MessageType } from '../../constants';
import moment from 'moment';

import {
  UploadToS3,
  Media,
  MessagePayload,
  FileUploadOutput,
} from '@zonofi/common';
import { getDocumentAsync, ResizeImages } from '@zonofi/seller-hub/helper';

import { getMediaType } from './get-media-type';
import { useGetPresignedUrl } from './get-presigned-url';

interface MessageInputCoreProps {
  channelId: string;
  conversationId: string;
  handleSendMsg: () => void;
}

export const useMessageInput = ({
  channelId,
  conversationId,
  handleSendMsg,
}: MessageInputCoreProps) => {
  const workspace = useAppSelector(getWorkspace);
  const waConfig = useAppSelector(getWaConfig);
  const sellerWorkspaceId = getWorkspaceId(store.getState());
  const { getPresignedUrl } = useGetPresignedUrl();
  const waConfigId = waConfig?.id;
  const user = workspace?.user;
  const userId = user?.id;
  const [file, setFile] = useState<FileUploadOutput[]>([]);

  const [publish, sendMsgRes] = usePublishMessageMutation();

  const [text, setText] = useState('');
  const [loader, setLoader] = useState(false);
  const [messageType, setMessageType] = useState<MessageType>(MessageType.TEXT);

  const handleInputChange = (newText: string) => {
    setText(newText);
  };

  const isValidInputText = useCallback(() => {
    return !!text.trim().length;
  }, [text]);

  const inputDisabled = useMemo(
    () => loader || !isValidInputText(),
    [isValidInputText, loader]
  );

  const handleFileChange = (file: FileUploadOutput[]) => {
    try {
      setMessageType(MessageType.MEDIA);
      setFile(file);
    } catch (e) {
      console.error(e);
    }
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = [...file];
    updatedFiles.splice(index, 1);
    setFile(updatedFiles);
  };

  const clearInput = () => {
    setFile([]);
    setText('');
  };

  const handleUploadMedia = async (
    onSuccess: (files: FileUploadOutput[]) => void
  ) => {
    const result = await getDocumentAsync({
      type: '*',
      multiple: true,
    });
    if (result) {
      const success = result.type === 'success';
      if (success && result?.output) {
        const files = await Promise.all(
          result?.output.map(async (f) => {
            const { file } = f;
            if (file.type.startsWith('image')) {
              const resizedPics = await ResizeImages([f]);
              //@ts-ignore
              f.uri = resizedPics?.[0]?.path;
            }
            return f;
          })
        );
        onSuccess(files);
      }
    }
  };

  const onUploadMedia = () => handleUploadMedia(handleFileChange);

  const sendMessage = async (selectedOption?: string) => {
    if (loader) return;
    if (!channelId) return;
    try {
      const messageId = uuidv4() as string;
      let message = {
        _id: messageId,
        text,
        type: messageType,
        source: 'system',
        sId: userId,
        cId: channelId,
        cAt: moment().valueOf(),
        user: {
          firstName: user?.firstName ?? '',
          lastName: user?.lastName ?? '',
          mobile: user?.mobile ?? '',
          email: user?.email ?? '',
          id: userId,
        },
        waConfigId: waConfigId,
        chnType: 'C',
        _v: 2,
      } as MessagePayload;
      setLoader(true);

      const files: Media[] = [];
      const localFiles: Media[] = [];
      if (file?.length) {
        try {
          await Promise.all(
            file?.map(async (f) => {
              const { file } = f;
              const fileId = uuidv4() as string;
              const fileInfo: Media = {
                _id: fileId,
                name: file.name,
                cntType: file.type,
                type: getMediaType(file.type),
              };
              const presignedUrl = await getPresignedUrl({
                name: file.name,
                cntType: file.type,
                type: getMediaType(file.type),
                method: 'put',
                channelId,
                messageId,
              });
              const uploadFile = { type: file.type, uri: f.uri as string };
              await UploadToS3(presignedUrl, uploadFile);

              files.push(fileInfo);
              localFiles.push({ ...fileInfo, path: f.uri as string });
            })
          );
          message = { ...message, media: files };
        } catch (error) {
          console.error(error);
        }
      }
      const payload = {
        sellerWorkspaceId,
        conversationId,
        channelId,
        message,
        isInternalNote: selectedOption === 'internalNotes',
      };
      publish(payload);

      clearInput();
    } catch (e) {
      console.error('ERROR: ', e);
    } finally {
      setLoader(false);
    }
    setText('');
    setMessageType(MessageType.TEXT);
  };

  useEffect(() => {
    if (sendMsgRes && sendMsgRes?.isSuccess) {
      handleSendMsg();
    }
  }, [sendMsgRes]);

  return {
    sendMessage,
    handleInputChange,
    text,
    isValidInputText,
    loader,
    inputDisabled,
    onUploadMedia,
    file,
    setFile,
  };
};
